import { useEffect, useState } from "react";

/*
 * The figures on a distribution band, read the way Billing reads them.
 *
 * Billing asks its endpoint once, for the page it is showing, and takes the
 * segment counts off that same response. Every other list used to ask again
 * per segment - `limit=1` and the row thrown away - which is five requests to
 * learn five numbers the list it already has could tell it.
 *
 * Nothing here fetches. A response carrying a counts map fills the whole band
 * at once; one that does not fills in the segment it was for, so the band
 * completes itself as the segments are visited. That is Billing's behaviour,
 * and it costs exactly one request either way.
 */

const COUNT_FIELDS = [
  "status_counts",
  "sale_type_counts",
  "tab_counts",
  "counts",
];

/** The status a bucket filters by, which is not always what it is keyed by. */
const statusOf = (bucket) => bucket.statuses ?? bucket.key;

/**
 * Per-bucket counts carried by a list response, keyed back to the buckets'
 * own keys. Null when the response carries none.
 */
export const countsFromResponse = (payload, buckets) => {
  const field = COUNT_FIELDS.find(
    (name) => payload?.[name] && typeof payload[name] === "object"
  );
  if (!field) return null;

  const map = payload[field];
  const counts = {};
  buckets.forEach((bucket) => {
    const value = map[statusOf(bucket)] ?? map[bucket.key];
    if (value != null) counts[bucket.key] = Number(value) || 0;
  });

  return Object.keys(counts).length ? counts : null;
};

/**
 * @param buckets   the band's segments: { key, statuses? }
 * @param response  the latest list response
 * @param activeKey the segment that response was filtered by, if any
 * @param search    a searched list counts matches, not the segment
 *
 * Returns counts keyed by bucket, plus ALL - the whole list, known whenever a
 * response came back unfiltered.
 */
export const useBucketCounts = ({ buckets, response, activeKey, search }) => {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!response || search) return;

    const count = Number(response.count) || 0;
    const served = countsFromResponse(response, buckets);

    setCounts((prev) =>
      served
        ? { ...prev, ...served, ALL: count }
        : { ...prev, [activeKey || "ALL"]: count }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, activeKey, search]);

  return counts;
};
